<template>
  <div class="stamp-preview">
    <div class="d-flex justify-content-between align-items-center">
      <label class="small font-weight-bold text-muted mb-0">Vista previa (arrastre los elementos para posicionarlos)</label>
      <span class="badge badge-light border text-muted ml-2" title="Ancho x alto que se está usando">
        {{ logicalWidth }} × {{ logicalHeight }}
      </span>
    </div>
    <div class="border rounded p-2 bg-white text-center mt-1 canvas-scroll">
      <canvas
        ref="canvas"
        :width="canvasWidth"
        :height="canvasHeight"
        :style="canvasStyle"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
      ></canvas>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StampPreviewCanvas',
  props: {
    template: { type: String, default: '' },
    representationUrl: { type: String, default: '' },
    backgroundUrl: { type: String, default: '' },
    // Dimensiones lógicas de la estampa (mismas unidades que usa el backend).
    // Por defecto 150x50, que es el tamaño default del firmador.
    stampWidth: { type: [Number, String], default: 150 },
    stampHeight: { type: [Number, String], default: 50 },
    // Zoom visual del configurador (1x..5x). Solo escala el render del canvas;
    // no afecta las dimensiones logicas ni la imagen final.
    zoom: { type: [Number, String], default: 1 },
    userData: {
      type: Object,
      default: () => ({ nombre: 'Nombre Usuario', cargo: 'Cargo', iut: '12345678-9', correo: 'usuario@empresa.cl' })
    },
    companyData: {
      type: Object,
      default: () => ({ razonSocial: 'Empresa S.A.' })
    }
  },
  data() {
    return {
      // Factor base de render (densidad de píxeles del canvas por unidad lógica).
      baseFactor: 3,
      backgroundImage: null,
      representationImage: null,
      elements: [],
      dragging: null,
      resizing: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
      hoveredIndex: null,
      hoverResize: false
    };
  },
  computed: {
    // Factor de render efectivo = densidad base × zoom del configurador.
    factor() {
      const z = parseInt(this.zoom, 10);
      const safeZoom = Number.isNaN(z) || z <= 0 ? 1 : z;
      return this.baseFactor * safeZoom;
    },
    logicalWidth() {
      const w = parseInt(this.stampWidth, 10);
      return Number.isNaN(w) || w <= 0 ? 150 : w;
    },
    logicalHeight() {
      const h = parseInt(this.stampHeight, 10);
      return Number.isNaN(h) || h <= 0 ? 50 : h;
    },
    canvasWidth() {
      return this.logicalWidth * this.factor;
    },
    canvasHeight() {
      return this.logicalHeight * this.factor;
    },
    canvasStyle() {
      // El canvas siempre se ajusta al ancho de su contenedor. El zoom se logra
      // haciendo crecer el contenedor (en ConfigureStamp), no el canvas, para
      // evitar reescalados que anulen el aumento.
      return {
        width: '100%',
        maxWidth: '100%',
        cursor: this.getCursor()
      };
    }
  },
  watch: {
    template: {
      handler() { this.$nextTick(() => { this.parseElements(); this.render(); }); },
      immediate: true
    },
    representationUrl() { this.loadRepresentation(); },
    backgroundUrl() { this.loadBackground(); },
    canvasWidth() { this.$nextTick(() => { this.parseElements(); this.render(); }); },
    canvasHeight() { this.$nextTick(() => { this.parseElements(); this.render(); }); }
  },
  methods: {
    resolveVariables(text) {
      return text
        .replace(/\$\{usuario\.Usuario\.nombre[^}]*\}/g, this.userData.nombre || 'Nombre')
        .replace(/\$\{usuario\.Usuario\.cargo[^}]*\}/g, this.userData.cargo || 'Cargo')
        .replace(/\$\{usuario\.Usuario\.iut[^}]*\}/g, this.userData.iut || '12345678-9')
        .replace(/\$\{usuario\.Usuario\.correo[^}]*\}/g, this.userData.correo || 'correo@empresa.cl')
        .replace(/\$\{empresa\.Empresa\.razonSocial[^}]*\}/g, this.companyData.razonSocial || 'Empresa')
        .replace(/\$\{formatDate\([^)]*\)\}/g, this.formatNow());
    },
    formatNow() {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    },
    parseElements() {
      if (!this.template || !this.template.trim()) {
        this.elements = [];
        return;
      }
      const lines = this.template.split('\n').filter(l => l.trim());
      this.elements = lines.map((line, index) => {
        const split = line.split('||||');
        if (split.length < 2) return null;
        const config = split[0].trim();
        const rawText = split[1].trim();
        const el = { index, size: 8, font: 'normal', color: 'black', x: 0, y: 15, type: 'T', ax: 100, ay: 25, text: rawText, bbox: null };

        config.split(';').forEach(pair => {
          const [key, val] = pair.split('=');
          if (!key || !val) return;
          switch (key.trim()) {
            case 'S': el.size = parseInt(val) || 8; break;
            case 'F':
              if (val === 'B') el.font = 'bold';
              else if (val === 'I') el.font = 'italic';
              else el.font = 'normal';
              break;
            case 'C': el.color = val.trim(); break;
            case 'X': { const n = parseInt(val); el.x = Number.isNaN(n) ? 0 : n; break; }
            case 'Y': { const n = parseInt(val); el.y = Number.isNaN(n) ? 15 : n; break; }
            case 'T': el.type = val.trim(); break;
            case 'AX': { const n = parseInt(val); el.ax = Number.isNaN(n) ? 100 : n; break; }
            case 'AY': { const n = parseInt(val); el.ay = Number.isNaN(n) ? 25 : n; break; }
          }
        });
        return el;
      }).filter(Boolean);
    },
    // Replica la semántica del backend (generarFolioImagenLegacy) para la
    // línea de firma manuscrita (T=F), en píxeles del canvas:
    //  - X/Y == -100  => auto-centrar en ese eje.
    //  - AX == 0 && AY == 0  => tamaño original de la imagen.
    //  - AX < 0  && AY < 0   => "fit": mantiene proporción y ajusta al alto de
    //                           la estampa.
    //  - en otro caso        => usa AX x AY tal cual.
    // Cuando no hay imagen (natW/natH = 0) se usa un placeholder con la misma
    // lógica de posición y un tamaño lógico por defecto para los casos 0/fit.
    computeSignatureRect(el, natW, natH) {
      const f = this.factor;
      const stampW = this.canvasWidth;
      const stampH = this.canvasHeight;
      const AUTO = -100;

      const autoCentrarX = el.x === AUTO;
      const autoCentrarY = el.y === AUTO;

      let w;
      let h;
      const tieneImagen = natW > 0 && natH > 0;

      if (el.ax === 0 && el.ay === 0) {
        // Tamaño original de la imagen (en px reales). Sin imagen: placeholder.
        w = tieneImagen ? natW : 100 * f;
        h = tieneImagen ? natH : 25 * f;
      } else if (el.ax < 0 && el.ay < 0) {
        // Fit: mantener proporción y ajustar al alto de la estampa.
        h = stampH;
        if (tieneImagen) {
          w = h * (natW / natH);
        } else {
          w = h * 3; // proporción por defecto del placeholder (3:1)
        }
      } else {
        // Ancho/alto explícitos (unidades lógicas -> px).
        w = el.ax * f;
        h = el.ay * f;
      }

      let x = autoCentrarX ? (stampW - w) / 2 : el.x * f;
      let y = autoCentrarY ? (stampH - h) / 2 : el.y * f;

      return { x, y, w, h };
    },
    render() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const w = this.canvasWidth;
      const h = this.canvasHeight;

      ctx.clearRect(0, 0, w, h);

      if (this.backgroundImage) {
        ctx.drawImage(this.backgroundImage, 0, 0, w, h);
      }

      for (const el of this.elements) {
        const px = el.x * this.factor;
        const py = el.y * this.factor;

        if (el.type === 'F') {
          if (this.representationImage) {
            const natW = this.representationImage.naturalWidth || this.representationImage.width || 0;
            const natH = this.representationImage.naturalHeight || this.representationImage.height || 0;
            const rect = this.computeSignatureRect(el, natW, natH);
            ctx.drawImage(this.representationImage, rect.x, rect.y, rect.w, rect.h);
            el.bbox = rect;
          } else {
            // Sin imagen cargada: dibujar un placeholder respetando la misma
            // semántica de posición/tamaño (fit, original y auto-centrado).
            const rect = this.computeSignatureRect(el, 0, 0);
            ctx.strokeStyle = '#ccc';
            ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
            ctx.fillStyle = '#999';
            ctx.font = '10px Helvetica, Arial, sans-serif';
            ctx.fillText('[firma]', rect.x + 5, rect.y + rect.h / 2);
            el.bbox = rect;
          }
        } else {
          const resolvedText = this.resolveVariables(el.text);
          const scaledSize = el.size * this.factor;
          ctx.font = `${el.font} ${scaledSize}px Helvetica, Arial, sans-serif`;
          ctx.fillStyle = el.color;
          ctx.fillText(resolvedText, px, py);
          const metrics = ctx.measureText(resolvedText);
          el.bbox = { x: px, y: py - scaledSize, w: metrics.width, h: scaledSize + 4 };
        }

        // Highlight hovered/dragged element
        if (el.index === this.hoveredIndex || el.index === this.dragging || el.index === this.resizing) {
          ctx.strokeStyle = 'rgba(115, 103, 240, 0.6)';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.strokeRect(el.bbox.x - 2, el.bbox.y - 2, el.bbox.w + 4, el.bbox.h + 4);
          ctx.setLineDash([]);

          // Resize handle for type F
          if (el.type === 'F') {
            const hx = el.bbox.x + el.bbox.w;
            const hy = el.bbox.y + el.bbox.h;
            ctx.fillStyle = 'rgba(115, 103, 240, 0.8)';
            ctx.fillRect(hx - 4, hy - 4, 8, 8);
          }
        }
      }
    },
    getCanvasCoords(event) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      };
    },
    hitTest(cx, cy) {
      for (let i = this.elements.length - 1; i >= 0; i--) {
        const el = this.elements[i];
        if (!el.bbox) continue;
        const { x, y, w, h } = el.bbox;
        if (cx >= x && cx <= x + w && cy >= y && cy <= y + h) {
          return i;
        }
      }
      return null;
    },
    isOnResizeHandle(cx, cy) {
      for (let i = this.elements.length - 1; i >= 0; i--) {
        const el = this.elements[i];
        if (el.type !== 'F' || !el.bbox) continue;
        const hx = el.bbox.x + el.bbox.w;
        const hy = el.bbox.y + el.bbox.h;
        if (Math.abs(cx - hx) < 8 && Math.abs(cy - hy) < 8) {
          return i;
        }
      }
      return null;
    },
    getCursor() {
      if (this.resizing !== null) return 'nwse-resize';
      if (this.dragging !== null) return 'grabbing';
      if (this.hoverResize) return 'nwse-resize';
      if (this.hoveredIndex !== null) return 'grab';
      return 'default';
    },
    onMouseDown(event) {
      const { x, y } = this.getCanvasCoords(event);
      // Check resize handle first
      const resizeIndex = this.isOnResizeHandle(x, y);
      if (resizeIndex !== null) {
        this.resizing = resizeIndex;
        return;
      }
      const hitIndex = this.hitTest(x, y);
      if (hitIndex !== null) {
        this.dragging = hitIndex;
        const el = this.elements[hitIndex];
        // Usar la posición realmente dibujada (bbox) como origen del arrastre.
        // Así los elementos auto-centrados (X/Y = -100) o con fit (AX/AY < 0)
        // no dan un salto al empezar a moverlos.
        const originX = el.bbox ? el.bbox.x : el.x * this.factor;
        const originY = el.bbox ? el.bbox.y : el.y * this.factor;
        this.dragOffsetX = x - originX;
        this.dragOffsetY = y - originY;
      }
    },
    onMouseMove(event) {
      const { x, y } = this.getCanvasCoords(event);

      if (this.resizing !== null) {
        const el = this.elements[this.resizing];
        // Origen del redimensionamiento = esquina dibujada (bbox), para soportar
        // elementos auto-centrados o con fit sin saltos.
        const originX = el.bbox ? el.bbox.x : el.x * this.factor;
        const originY = el.bbox ? el.bbox.y : el.y * this.factor;
        const newW = Math.max(20, Math.round((x - originX) / this.factor));
        const newH = Math.max(10, Math.round((y - originY) / this.factor));
        el.ax = newW;
        el.ay = newH;
        this.render();
      } else if (this.dragging !== null) {
        const el = this.elements[this.dragging];
        if (el.type === 'F') {
          const newX = Math.round((x - this.dragOffsetX) / this.factor);
          const newY = Math.round((y - this.dragOffsetY) / this.factor);
          el.x = newX;
          el.y = newY;
        } else {
          const newX = Math.max(0, Math.round((x - this.dragOffsetX) / this.factor));
          const scaledSize = el.size * this.factor;
          const newY = Math.max(0, Math.round((y - this.dragOffsetY + scaledSize) / this.factor));
          el.x = newX;
          el.y = newY;
        }
        this.render();
      } else {
        const resizeHit = this.isOnResizeHandle(x, y);
        this.hoverResize = resizeHit !== null;
        const hitIndex = this.hitTest(x, y);
        if (hitIndex !== this.hoveredIndex || this.hoverResize) {
          this.hoveredIndex = hitIndex;
          this.render();
        }
      }
    },
    onMouseUp() {
      if (this.resizing !== null) {
        const el = this.elements[this.resizing];
        this.$emit('update-size', el.index, el.ax, el.ay);
        this.resizing = null;
      } else if (this.dragging !== null) {
        const el = this.elements[this.dragging];
        this.$emit('update-position', el.index, el.x, el.y);
        this.dragging = null;
      }
    },
    loadBackground() {
      if (!this.backgroundUrl) { this.backgroundImage = null; this.render(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.backgroundImage = img; this.render(); };
      img.onerror = () => { this.backgroundImage = null; this.render(); };
      img.src = this.backgroundUrl;
    },
    loadRepresentation() {
      if (!this.representationUrl) { this.representationImage = null; this.render(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.representationImage = img; this.render(); };
      img.onerror = () => { this.representationImage = null; this.render(); };
      img.src = this.representationUrl;
    }
  },
  mounted() {
    this.loadBackground();
    this.loadRepresentation();
    this.$nextTick(() => { this.parseElements(); this.render(); });
  }
};
</script>

<style scoped>
.stamp-preview canvas {
  border: 1px dashed #ddd;
}
.canvas-scroll {
  overflow: auto;
}
</style>
